import { SessionsUI, useExamSessions } from './useExamSessions';
import { MultiSelectFilterProps } from '../../../shared/components/MultiSelect';
import { AnomalyTypeAPI, AuditStatus, fromEnumToReadableString } from '@shared';
import { useRemoteFilter } from '../../../shared/components/MultiSelect/useRemoteFilter';
import { examSessionsApi } from '../../../App';
import { useMemo } from 'react';

const ANOMALY_COLUMN_ID = 'anomaly_count';
const AUDIT_STATUS_COLUMN_ID = 'audit_status';

type UseExamSessionsFiltersArgs = {
  exam: string;
  partner: string;
  course: string;
  orderBy: string;
  order: 'asc' | 'desc';
  rowsPerPage: number;
  auditOfInterest: boolean;
  pageNavigationTool: { page: number; setPage: (p: number) => void };
};

type UseExamSessionsFiltersResult = {
  data: SessionsUI | undefined;
  isLoading: boolean;
  isError: boolean;
  filters: Array<MultiSelectFilterProps>;
};

export const useExamSessionsFilters = ({
  exam,
  partner,
  course,
  orderBy,
  order,
  rowsPerPage,
  auditOfInterest,
  pageNavigationTool,
}: UseExamSessionsFiltersArgs): UseExamSessionsFiltersResult => {
  const { page, setPage } = pageNavigationTool;

  const {
    filterProps: auditStatusFilterProps,
    isError: isAuditFilterError,
    isLoading: isAuditFilterLoading,
  } = useRemoteFilter<AuditStatus>({
    key: ['exam audit statuses', exam],
    query: () => examSessionsApi.getAuditStatusesByExamUuid(exam),
    mapOption: (a) => ({ label: fromEnumToReadableString(a), value: a }),
    selectorLabel: 'Audit Status',
    persistKey: `auditStatuses_${exam}`,
    id: AUDIT_STATUS_COLUMN_ID,
  });

  const {
    filterProps: anomalyFilterProps,
    isError: isAnomalyFilterError,
    isLoading: isAnomalyFilterLoading,
  } = useRemoteFilter<AnomalyTypeAPI>({
    key: ['anomaly types', exam],
    query: () => examSessionsApi.getAnomalyTypesByExamUuid(exam, true),
    mapOption: (a) => ({ label: a.label, value: String(a.id) }),
    selectorLabel: 'Anomalies',
    persistKey: `anomalyIds_${exam}`,
    id: ANOMALY_COLUMN_ID,
    groupBy: (item) => (item.enabled ? 'Enabled' : 'Disabled'),
    groupSort: ([a], [b]) => (a === 'Enabled' ? -1 : b === 'Enabled' ? 1 : 0),
  });

  const shouldRunQuery = !isAuditFilterLoading && !isAnomalyFilterLoading;

  const auditStatusesToLoadExamSessions = useMemo(() => {
    const result = auditStatusFilterProps.selectedOptions?.map((a) => a.value) ?? [];
    setPage(1);
    return result;
  }, [auditStatusFilterProps.selectedOptions, setPage]);

  const anomalyIdsToLoadExamSessions = useMemo(() => {
    const result = anomalyFilterProps.selectedOptions?.map((a) => Number(a.value)) ?? [];
    setPage(1);
    return result;
  }, [anomalyFilterProps.selectedOptions, setPage]);

  const {
    data,
    isLoading: isExamSessionsLoading,
    isError,
  } = useExamSessions({
    partner,
    course,
    exam,
    params: {
      field: orderBy,
      direction: order,
      offset: page - 1,
      limit: rowsPerPage,
      auditOfInterest: auditOfInterest || false,
      anomalyTypes: anomalyIdsToLoadExamSessions,
      auditStatuses: auditStatusesToLoadExamSessions,
    },
    shouldRunQuery,
  });

  const isLoading = isAuditFilterLoading || isAnomalyFilterLoading || isExamSessionsLoading;

  return {
    data,
    filters: [anomalyFilterProps, auditStatusFilterProps],
    isError: isError || isAnomalyFilterError || isAuditFilterError,
    isLoading,
  };
};
